// Attaches the Okta ID token to same-origin backend calls (/api and /sessionm) so
// the server can verify the caller is authenticated. The app makes ~60 raw fetch()
// calls with no auth header; installing a single window.fetch interceptor covers them
// all without touching each call site. Imported first in index.tsx.

const OKTA_STORAGE_KEY = 'okta-token-storage'

function getIdToken(): string | null {
  try {
    const raw = localStorage.getItem(OKTA_STORAGE_KEY) || sessionStorage.getItem(OKTA_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    // okta-auth-js stores { idToken: { idToken: '<jwt>', ... }, accessToken: {...} }
    return parsed?.idToken?.idToken || null
  } catch {
    return null
  }
}

function requestUrl(input: RequestInfo | URL): URL | null {
  try {
    if (typeof input === 'string') return new URL(input, window.location.href)
    if (input instanceof URL) return input
    if (input instanceof Request) return new URL(input.url, window.location.href)
  } catch {
    /* ignore */
  }
  return null
}

function isSameOriginBackend(input: RequestInfo | URL): boolean {
  const url = requestUrl(input)
  return !!url
    && url.origin === window.location.origin
    && (url.pathname.startsWith('/api') || url.pathname.startsWith('/sessionm'))
}

function requestMethod(input: RequestInfo | URL, init?: RequestInit): string {
  return String(init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase()
}

function createOperationId(): string {
  const bytes = new Uint8Array(16)
  try {
    if (typeof window.crypto?.randomUUID === 'function') return window.crypto.randomUUID()
    if (typeof window.crypto?.getRandomValues === 'function') {
      window.crypto.getRandomValues(bytes)
    } else {
      bytes.forEach((_, index) => { bytes[index] = Math.floor(Math.random() * 256) })
    }
  } catch {
    bytes.forEach((_, index) => { bytes[index] = Math.floor(Math.random() * 256) })
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
  const originalFetch = window.fetch.bind(window)
  window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    try {
      if (isSameOriginBackend(input)) {
        const token = getIdToken()
        const headers = new Headers(
          (init && init.headers) || (input instanceof Request ? input.headers : undefined)
        )
        if (token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`)
        if (
          ['POST', 'PUT', 'PATCH', 'DELETE'].includes(requestMethod(input, init))
          && !headers.has('Idempotency-Key')
        ) {
          headers.set('Idempotency-Key', createOperationId())
        }
        return originalFetch(input as RequestInfo, { ...(init || {}), headers })
      }
    } catch {
      /* fall through to the original fetch on any error */
    }
    return originalFetch(input as RequestInfo, init)
  }
}

export { createOperationId, isSameOriginBackend, requestMethod, requestUrl }
